import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type QualityRange,
  useQualityDateRange,
} from '@/features/quality/hooks/useQualityDateRange';
import { usePersistedStringArray } from '@/shared/hooks';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildGridQueryInput } from '@/ui/composites/grid/gridUtils';
import { useEntityTrendsDateSelection } from './useEntityTrendsDateSelection';
import {
  buildEntityStatusFilters,
  buildExecutionStatusFilters,
  buildRelatedEntitySummaryFilters,
  buildSelectedDayFilters,
  getExecutionStatusSelection,
} from './useEntityTrendsPage.helpers';
import type {
  ActiveEntityTab,
  EntityStatusKey,
  EntityTrendsQueryModelConfig,
  EntityTrendsQueryModelResult,
} from './useEntityTrendsPage.types';
import type { MetricsRange } from '@/features/metrics';
import type { EntityChartTab } from '@/features/shared/components/entityTrends/EntityPageContent';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';

export const useEntityTrendsQueryModel = ({
  gridPath,
  entitySearchField,
  executionSearchField,
}: EntityTrendsQueryModelConfig): EntityTrendsQueryModelResult => {
  const [range, setRange] = useState<QualityRange>('latest');
  const [activeTab, setActiveTab] = useState<ActiveEntityTab>('entity');
  const [chartTab, setChartTab] = useState<EntityChartTab>('status');
  const [suppressChartAnimation, setSuppressChartAnimation] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<EntityStatusKey[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray(`tag-filter:${gridPath}`);
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useQualityDateRange(range);
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const {
    selectedStartDay,
    selectedEndDay,
    chartSelectedStartDay,
    chartSelectedEndDay,
    setSelectedStartDay,
    setSelectedEndDay,
    setChartSelectedStartDay,
    setChartSelectedEndDay,
    clearSelectedAndChartDates,
  } = useEntityTrendsDateSelection({ isLatestMode });
  const showDelta = !isLatestMode && !isAllMode && previousRangeStartIso !== null;

  const dateRangeFilters = useMemo(
    () => buildDateRangeFilters('timestamp', rangeStartIso, rangeEndIso),
    [rangeEndIso, rangeStartIso],
  );
  const previousDateRangeFilters = useMemo(
    () => buildDateRangeFilters('timestamp', previousRangeStartIso, previousRangeEndIso),
    [previousRangeEndIso, previousRangeStartIso],
  );
  const selectedDayFilters = useMemo(
    () => buildSelectedDayFilters(selectedStartDay, selectedEndDay),
    [selectedEndDay, selectedStartDay],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [searchValue]);

  useEffect(() => {
    setSuppressChartAnimation(true);
    const timer = window.setTimeout(() => {
      setSuppressChartAnimation(false);
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [chartTab]);

  const entitySearchFilters = useMemo(
    () => buildContainsFilter(entitySearchField, debouncedSearchValue),
    [debouncedSearchValue, entitySearchField],
  );
  const executionSearchFilters = useMemo(
    () => buildContainsFilter(executionSearchField, debouncedSearchValue),
    [debouncedSearchValue, executionSearchField],
  );
  const entityStatusFilters = useMemo(
    () => buildEntityStatusFilters(selectedStatuses),
    [selectedStatuses],
  );
  const executionStatusFilters = useMemo(
    () => buildExecutionStatusFilters(selectedStatuses),
    [selectedStatuses],
  );
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const summaryFilters = useMemo(
    () =>
      isLatestMode
        ? mergeFilters(entitySearchFilters, entityStatusFilters, tagFilters)
        : mergeFilters(
            dateRangeFilters,
            executionSearchFilters,
            executionStatusFilters,
            tagFilters,
          ),
    [
      dateRangeFilters,
      entitySearchFilters,
      entityStatusFilters,
      executionSearchFilters,
      executionStatusFilters,
      isLatestMode,
      tagFilters,
    ],
  );
  const previousSummaryFilters = useMemo(
    () =>
      mergeFilters(
        previousDateRangeFilters,
        executionSearchFilters,
        executionStatusFilters,
        tagFilters,
      ),
    [executionSearchFilters, executionStatusFilters, previousDateRangeFilters, tagFilters],
  );
  const baseExecutionFilters = useMemo(
    () =>
      mergeFilters(
        selectedDayFilters,
        !isLatestMode && !isAllMode ? dateRangeFilters : undefined,
        isLatestMode ? buildEqFilterValue('isLatest', true) : undefined,
        executionSearchFilters,
        tagFilters,
      ),
    [
      dateRangeFilters,
      executionSearchFilters,
      isAllMode,
      isLatestMode,
      selectedDayFilters,
      tagFilters,
    ],
  );
  const summaryInput = useMemo(
    () => buildGridQueryInput({ page: 1, pageSize: 1, sorts: [], filters: summaryFilters }),
    [summaryFilters],
  );
  const previousSummaryInput = useMemo(
    () =>
      isLatestMode
        ? null
        : buildGridQueryInput({
            page: 1,
            pageSize: 1,
            sorts: [],
            filters: previousSummaryFilters,
          }),
    [isLatestMode, previousSummaryFilters],
  );
  const summaryFiltersKey = useMemo(
    () => JSON.stringify(summaryInput.filters),
    [summaryInput.filters],
  );
  const previousSummaryFiltersKey = previousSummaryInput
    ? JSON.stringify(previousSummaryInput.filters)
    : 'latest';
  const relatedEntitySummaryFilters = useMemo<GridQueryInput['filters']>(
    () =>
      buildRelatedEntitySummaryFilters({
        isLatestMode,
        entityFilters: mergeFilters(entitySearchFilters, entityStatusFilters, tagFilters),
        executionFilters: mergeFilters(
          dateRangeFilters,
          executionSearchFilters,
          executionStatusFilters,
          tagFilters,
        ),
        entitySearchField,
        executionSearchField,
      }),
    [
      dateRangeFilters,
      entitySearchField,
      entitySearchFilters,
      entityStatusFilters,
      executionSearchField,
      executionSearchFilters,
      executionStatusFilters,
      isLatestMode,
      tagFilters,
    ],
  );
  const relatedEntitySummaryInput = useMemo(
    () =>
      buildGridQueryInput({
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: relatedEntitySummaryFilters,
      }),
    [relatedEntitySummaryFilters],
  );
  const relatedEntitySummaryFiltersKey = useMemo(
    () => JSON.stringify(relatedEntitySummaryInput.filters),
    [relatedEntitySummaryInput.filters],
  );
  const entityTableFilters = useMemo(
    () => mergeFilters(entitySearchFilters, entityStatusFilters, tagFilters),
    [entitySearchFilters, entityStatusFilters, tagFilters],
  );
  const executionStatusSelection = useMemo(
    () => getExecutionStatusSelection(selectedStatuses),
    [selectedStatuses],
  );
  const isUntestedOnly = selectedStatuses.length === 1 && selectedStatuses[0] === 'untested';
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);
  const metricsRange = useMemo<MetricsRange>(
    () => (hasCustomDates ? 'all' : isLatestMode ? 'all' : range),
    [hasCustomDates, isLatestMode, range],
  );

  const handleRangeChange = useCallback((nextRange: QualityRange) => {
    setRange(nextRange);
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleChartTabChange = useCallback((nextTab: EntityChartTab) => {
    setChartTab(nextTab);
  }, []);

  const handleActiveTabChange = useCallback((nextTab: ActiveEntityTab) => {
    setActiveTab(nextTab);
  }, []);

  const handleStatusToggle = useCallback((status: EntityStatusKey) => {
    setSelectedStatuses((previousStatuses) =>
      previousStatuses.includes(status)
        ? previousStatuses.filter((item) => item !== status)
        : [...previousStatuses, status],
    );
  }, []);

  const handleStatusesChange = useCallback((nextStatuses: EntityStatusKey[]) => {
    setSelectedStatuses(nextStatuses);
  }, []);

  const handleTagsChange = useCallback(
    (nextTags: string[]) => {
      setSelectedTags(nextTags);
    },
    [setSelectedTags],
  );

  return {
    range,
    activeTab,
    chartTab,
    suppressChartAnimation,
    searchValue,
    debouncedSearchValue,
    selectedStatuses,
    selectedTags,
    rangeStartIso,
    rangeEndIso,
    previousRangeStartIso,
    previousRangeEndIso,
    selectedStartDay,
    selectedEndDay,
    chartSelectedStartDay,
    chartSelectedEndDay,
    setSelectedStartDay,
    setSelectedEndDay,
    setChartSelectedStartDay,
    setChartSelectedEndDay,
    clearSelectedAndChartDates,
    isLatestMode,
    isAllMode,
    showDelta,
    hasCustomDates,
    isUntestedOnly,
    summaryInput,
    previousSummaryInput,
    summaryFiltersKey,
    previousSummaryFiltersKey,
    relatedEntitySummaryInput,
    relatedEntitySummaryFiltersKey,
    entityTableFilters,
    baseExecutionFilters,
    executionStatusFilters,
    executionStatusSelection,
    metricsRange,
    handleRangeChange,
    handleSearchChange,
    handleChartTabChange,
    handleActiveTabChange,
    handleStatusToggle,
    handleStatusesChange,
    handleTagsChange,
  };
};
